import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CompanySize, CompanySizeValues } from '@paladin-monorepo/common';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { accountsApi } from '@/lib/api/accounts';
import { codebookApi } from '@/lib/api/codebook';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

const NONE = '__none__';

interface AccountCompanyProfileSettingsProps {
  accountId: string;
}

export function AccountCompanyProfileSettings({ accountId }: AccountCompanyProfileSettingsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: account, isLoading: accountLoading } = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => accountsApi.get(accountId),
    enabled: !!accountId,
  });

  const { data: industries = [], isLoading: industriesLoading } = useQuery({
    queryKey: ['codebook', 'industries'],
    queryFn: () => codebookApi.listIndustries(),
  });

  const [industryId, setIndustryId] = useState<string>(NONE);
  const [companySize, setCompanySize] = useState<string>(NONE);

  const serverIndustry = account?.companyIndustryId ?? null;
  const serverSize = account?.companySize ?? null;

  useEffect(() => {
    setIndustryId(serverIndustry && serverIndustry !== '' ? serverIndustry : NONE);
  }, [serverIndustry]);

  useEffect(() => {
    setCompanySize(serverSize && serverSize !== '' ? serverSize : NONE);
  }, [serverSize]);

  const isDirty = useMemo(() => {
    const curInd = industryId === NONE ? null : industryId;
    const curSize = companySize === NONE ? null : (companySize as CompanySize);
    return curInd !== serverIndustry || curSize !== serverSize;
  }, [industryId, companySize, serverIndustry, serverSize]);

  const updateMutation = useMutation({
    mutationFn: () =>
      accountsApi.update(accountId, {
        companyIndustryId: industryId === NONE ? null : industryId,
        companySize: companySize === NONE ? null : (companySize as CompanySize),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', accountId] });
      queryClient.invalidateQueries({ queryKey: ['/me'] });
      toast.success(t('account.settings.companyProfile.saved'));
    },
    onError: () => {
      toast.error(t('account.settings.companyProfile.saveFailed'));
    },
  });

  if (accountLoading || !account) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('account.settings.companyProfile.title')}</CardTitle>
        <CardDescription>{t('account.settings.companyProfile.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <div className="flex flex-col gap-2 max-w-md">
          <Label>{t('account.settings.companyProfile.industryLabel')}</Label>
          <Select
            value={industryId}
            onValueChange={setIndustryId}
            disabled={industriesLoading}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('account.settings.companyProfile.industryPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{t('account.settings.companyProfile.industryNone')}</SelectItem>
              {industries.map((i) => (
                <SelectItem key={i.id} value={i.id}>
                  {t(`projects.industry.options.${i.id}`, { defaultValue: i.id })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-2 max-w-md">
          <Label>{t('account.settings.companyProfile.sizeLabel')}</Label>
          <Select value={companySize} onValueChange={setCompanySize}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('account.settings.companyProfile.sizePlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{t('account.settings.companyProfile.sizeNone')}</SelectItem>
              {CompanySizeValues.map((size) => (
                <SelectItem key={size} value={size}>
                  {t(`account.settings.companyProfile.size.${size}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isDirty && (
          <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? t('generic.saving') : t('generic.save')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
